import React from 'react';
import { Badge, Tooltip } from '@chakra-ui/react';

/**
 * The 24-hour customer service window.
 *
 * Outside it WhatsApp only permits an approved template, so the composer
 * changes shape — this chip is what tells the agent WHY before they type a
 * message that would be refused.
 *
 * The window is computed from `lastInboundAt`, never `lastMessageAt`: only the
 * customer writing to us reopens it. Using the latter would show a green chip
 * because we replied, and the send would then fail.
 */

const WINDOW_MS = 24 * 60 * 60 * 1000;
// Warn before it closes, so an agent can finish a conversation rather than
// discovering the limit mid-sentence.
const WARN_MS = 2 * 60 * 60 * 1000;

/**
 * ★ `everInbound` separates two states that look identical from `open` alone:
 * a window that CLOSED, and one that never opened because the customer has
 * never written in. "Reopen this conversation" is wrong for a thread that was
 * never open, and that is exactly what an agent sees the first time they start
 * a chat. Callers that only gate typing can keep reading `open`.
 */
export function windowState(lastInboundAt, now = Date.now()) {
	if (!lastInboundAt) return { open: false, remainingMs: 0, everInbound: false };
	const remainingMs = WINDOW_MS - (now - new Date(lastInboundAt).getTime());
	return { open: remainingMs > 0, remainingMs, everInbound: true };
}

const format = (ms) => {
	const hours = Math.floor(ms / 3600000);
	if (hours >= 1) return `${hours}h left`;
	const minutes = Math.max(1, Math.floor(ms / 60000));
	return `${minutes}m left`;
};

export default function WindowChip({ lastInboundAt }) {
	const { open, remainingMs, everInbound } = windowState(lastInboundAt);

	if (!everInbound) {
		return (
			<Tooltip label="This chat was started from here. The customer has not replied, so the 24-hour window has not opened yet.">
				<Badge colorScheme="blue" variant="subtle" borderRadius="full" px="2">
					Awaiting reply
				</Badge>
			</Tooltip>
		);
	}

	if (!open) {
		return (
			<Tooltip label="Outside the 24-hour window — only an approved template can be sent.">
				<Badge colorScheme="orange" variant="subtle" borderRadius="full" px="2">
					Template only
				</Badge>
			</Tooltip>
		);
	}

	const closingSoon = remainingMs <= WARN_MS;
	return (
		<Tooltip label="Time left to reply with a free-form message.">
			<Badge
				colorScheme={closingSoon ? 'yellow' : 'green'}
				variant="subtle"
				borderRadius="full"
				px="2"
			>
				{format(remainingMs)}
			</Badge>
		</Tooltip>
	);
}
